import {
  type UseMutationOptions,
  type UseMutationResult,
  useMutation,
} from "@tanstack/react-query";
import {
  ApiRequestError,
  assertOperatorIdentityUnchanged,
  currentOperatorIdentityEpoch,
} from "./api";

export function isOperatorIdentityChangedError(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === "OPERATOR_IDENTITY_CHANGED";
}

interface OperatorMutationContext<TContext> {
  identityEpochAtDispatch: number;
  callbackContext: TContext | undefined;
}

export function guardOperatorMutationOptions<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationOptions<TData, TError, TVariables, OperatorMutationContext<TContext>> {
  const { mutationFn, onError, onMutate, onSettled, onSuccess, ...baseOptions } = options;
  return {
    ...baseOptions,
    ...(mutationFn
      ? {
          mutationFn: async (variables: TVariables) => {
            const identityEpochAtDispatch = currentOperatorIdentityEpoch();
            try {
              const data = await mutationFn(variables);
              assertOperatorIdentityUnchanged(identityEpochAtDispatch);
              return data;
            } catch (error) {
              assertOperatorIdentityUnchanged(identityEpochAtDispatch);
              throw error;
            }
          },
        }
      : {}),
    onMutate: async (variables) => {
      const identityEpochAtDispatch = currentOperatorIdentityEpoch();
      const callbackContext = await onMutate?.(variables);
      assertOperatorIdentityUnchanged(identityEpochAtDispatch);
      return { identityEpochAtDispatch, callbackContext };
    },
    onSuccess: async (data, variables, context) => {
      if (context.identityEpochAtDispatch !== currentOperatorIdentityEpoch()) return;
      await onSuccess?.(data, variables, context.callbackContext as TContext);
    },
    onError: async (error, variables, context) => {
      if (
        isOperatorIdentityChangedError(error) ||
        (context !== undefined &&
          context.identityEpochAtDispatch !== currentOperatorIdentityEpoch())
      ) {
        return;
      }
      await onError?.(error, variables, context?.callbackContext);
    },
    onSettled: async (data, error, variables, context) => {
      if (
        isOperatorIdentityChangedError(error) ||
        (context !== undefined &&
          context.identityEpochAtDispatch !== currentOperatorIdentityEpoch())
      ) {
        return;
      }
      await onSettled?.(data, error, variables, context?.callbackContext);
    },
  };
}

export function useOperatorMutation<
  TData = unknown,
  TError = Error,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const mutation = useMutation(guardOperatorMutationOptions(options));
  return {
    ...mutation,
    context: mutation.context?.callbackContext,
  } as UseMutationResult<TData, TError, TVariables, TContext>;
}
